import { apiSlice } from '../api/apiSlice';

export const marketingApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMarketerByMarketerId: builder.query({
      query: (marketerId) => ({
        url: '/marketingagent/getOnemarketerbyMarketId',
        params: { marketerId },
      }),
      providesTags: (result, error, marketerId) => {
        const agent = result?.data;
        const tags = [{ type: 'Marketer', id: marketerId }];
        if (agent?._id) tags.push({ type: 'Marketer', id: String(agent._id) });
        if (agent?.marketerId)
          tags.push({ type: 'Marketer', id: String(agent.marketerId) });
        return tags;
      },
    }),

    /** Per-marketer rating (embedded on MarketingAgent). Upserts by shopid. */
    addOrUpdateMarketerRating: builder.mutation({
      query: ({ marketerId, shopid, rating, comment }) => ({
        url: '/marketingagent/rating',
        method: 'POST',
        params: { marketerId },
        body: { shopid, rating, comment },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const cacheKeys = [
          arg.cacheQueryId,
          arg.agentMongoId,
          arg.publicMarketerId,
          arg.marketerId,
        ].filter(Boolean);
        const uniqueKeys = [...new Set(cacheKeys.map(String))];
        const shopKey = String(arg.shopid);
        const nextReview = {
          shopid: arg.shopid,
          rating: Number(arg.rating),
          comment: arg.comment || '',
          createdAt: new Date().toISOString(),
        };

        const patchRatingDraft = (draft) => {
          if (!draft) return;
          const reviews = Array.isArray(draft.ratings)
            ? [...draft.ratings]
            : Array.isArray(draft.data?.ratingReviews)
              ? [...draft.data.ratingReviews]
              : [];
          const idx = reviews.findIndex(
            (r) => String(r?.shopid?._id || r?.shopid) === shopKey,
          );
          if (idx >= 0) reviews[idx] = { ...reviews[idx], ...nextReview };
          else reviews.push(nextReview);

          const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          let sum = 0;
          reviews.forEach((r) => {
            const n = Number(r.rating);
            if (breakdown[n] !== undefined) {
              breakdown[n] += 1;
              sum += n;
            }
          });
          const totalReviews = reviews.length;
          draft.ratings = reviews;
          draft.topReviewSection = {
            totalReviews,
            averageRating:
              totalReviews > 0 ? Number((sum / totalReviews).toFixed(1)) : 0,
            ratingRatio: {
              oneStar: breakdown[1],
              twoStar: breakdown[2],
              threeStar: breakdown[3],
              fourStar: breakdown[4],
              fiveStar: breakdown[5],
            },
          };
          if (draft.data) {
            draft.data.ratingReviews = reviews;
          }
        };

        const patches = uniqueKeys.map((key) =>
          dispatch(
            apiSlice.util.updateQueryData(
              'getMarketerByMarketerId',
              key,
              patchRatingDraft,
            ),
          ),
        );

        // Shop Side hired list — show review, hide textarea immediately
        const patchHiredDraft = (draft) => {
          if (!draft?.data || !Array.isArray(draft.data)) return;
          const agentKey = String(
            arg.agentMongoId || arg.cacheQueryId || arg.marketerId || '',
          );
          const publicKey = String(arg.publicMarketerId || arg.marketerId || '');
          draft.data = draft.data.map((row) => {
            const match =
              String(row.agentMongoId) === agentKey ||
              String(row.marketerId) === publicKey ||
              String(row.agentMongoId) === publicKey;
            if (!match) return row;
            const hadMine = !!row.myShopReview;
            const prevAvg = Number(row.averageRating) || 0;
            const prevTotal = Number(row.totalReviews) || 0;
            const nextTotal = hadMine ? prevTotal : prevTotal + 1;
            const nextAvg =
              nextTotal > 0
                ? Number(
                    (
                      (prevAvg * prevTotal -
                        (hadMine ? Number(row.myShopReview.rating) || 0 : 0) +
                        Number(arg.rating)) /
                      nextTotal
                    ).toFixed(1),
                  )
                : Number(arg.rating);
            return {
              ...row,
              averageRating: nextAvg,
              totalReviews: nextTotal,
              myShopReview: {
                rating: Number(arg.rating),
                comment: arg.comment || '',
                createdAt: nextReview.createdAt,
              },
            };
          });
        };
        const hiredShopKeys = [
          shopKey,
          localStorage.getItem('OpenShopIdForMarketingByMarketer'),
          localStorage.getItem('shopid'),
        ].filter(Boolean);
        [...new Set(hiredShopKeys.map(String))].forEach((key) => {
          patches.push(
            dispatch(
              apiSlice.util.updateQueryData(
                'getHiredMarketersByShop',
                key,
                patchHiredDraft,
              ),
            ),
          );
        });

        try {
          const { data } = await queryFulfilled;
          if (data?.topReviewSection) {
            uniqueKeys.forEach((key) => {
              dispatch(
                apiSlice.util.updateQueryData(
                  'getMarketerByMarketerId',
                  key,
                  (draft) => {
                    if (!draft) return;
                    draft.topReviewSection = data.topReviewSection;
                    if (Array.isArray(data.data)) {
                      draft.ratings = data.data;
                      if (draft.data) draft.data.ratingReviews = data.data;
                    }
                  },
                ),
              );
            });
          }
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (result, error, arg) => {
        const ids = [
          arg.cacheQueryId,
          arg.agentMongoId,
          arg.publicMarketerId,
          arg.marketerId,
          result?.agentMongoId,
          result?.marketerId,
        ].filter(Boolean);
        const tags = [...new Set(ids.map(String))].map((id) => ({
          type: 'Marketer',
          id,
        }));
        if (arg.shopid) {
          tags.push({ type: 'HireRequest', id: `shop-${arg.shopid}` });
        }
        return tags;
      },
    }),

    getHiredMarketersByShop: builder.query({
      query: (shopId) => ({
        url: '/hirerequest/by-shop',
        params: { shopId },
      }),
      providesTags: (result, error, shopId) => [
        { type: 'HireRequest', id: `shop-${shopId}` },
        'HireRequest',
      ],
    }),

    getMarketersByBusinessType: builder.query({
      query: (arg) => {
        const businessType = typeof arg === 'object' ? arg?.businessType : arg;
        const shopId = typeof arg === 'object' ? arg?.shopId : undefined;
        return {
          url: '/marketingagent/getallmarketerbyBT',
          params: { businessType, ...(shopId ? { shopId } : {}) },
        };
      },
      providesTags: ['Marketers'],
    }),

    getSubscriptionMarketing: builder.query({
      query: () => ({
        url: '/subscriptionMarketing',
      }),
      providesTags: ['SubscriptionMarketing'],
    }),

    checkMarketerSubscriptionStatus: builder.query({
      query: (shopid) => ({
        url: '/subscriptionMarketing/purchases-by-shop-Checking',
        params: { shopid },
      }),
    }),

    getSubscriptionPurchaseByShop: builder.query({
      query: (shopid) => ({
        url: '/subscriptionMarketing/purchases-by-shop',
        params: { shopid },
      }),
      providesTags: ['SubscriptionMarketing'],
    }),

    purchaseMarketerPlan: builder.mutation({
      query: (payload) => ({
        url: '/subscriptionMarketing/purchaseplan',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SubscriptionMarketing'],
    }),

    hireMarketer: builder.mutation({
      query: (data) => ({
        url: '/marketingagent/hire',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Marketer'],
    }),

    getMarketerByUserId: builder.query({
      query: (userid) => ({
        url: '/marketingagent',
        params: { userid },
      }),
      providesTags: ['Marketers', 'Marketer'],
    }),

    // ==============================
    // HIRE REQUEST ENDPOINTS
    // ==============================
    createHireRequest: builder.mutation({
      query: (payload) => ({
        url: '/hirerequest',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['HireRequest'],
    }),

    getHireRequestStatus: builder.query({
      query: ({ shopId, marketerId }) => ({
        url: '/hirerequest/status',
        params: { shopId, marketerId },
      }),
      providesTags: (result, error, { shopId, marketerId }) => [
        { type: 'HireRequest', id: `${shopId}-${marketerId}` },
      ],
    }),

    getPurchaseByMarketer: builder.query({
      query: (marketId) => ({
        url: '/subscriptionMarketing/GetPurchasebyMarketer',
        params: { marketId },
      }),
      providesTags: ['SubscriptionMarketing'],
    }),

    getPurchasesByUser: builder.query({
      query: (userid) => ({
        url: '/subscriptionMarketing/purchases-by-user',
        params: { userid },
      }),
      providesTags: ['SubscriptionMarketing'],
    }),

    updateTaskStatus: builder.mutation({
      query: ({ purchaseId, taskStatus }) => ({
        url: '/subscriptionMarketing/update-task-status',
        method: 'PUT',
        params: { purchaseId, taskStatus },
      }),
      invalidatesTags: ['SubscriptionMarketing'],
    }),

    downloadCustomerData: builder.query({
      query: (params) => ({
        url: '/marketingagent/customer-data/download',
        params, // targetZoneId, userid
      }),
    }),

    checkZoneConnections: builder.query({
      query: () => ({
        url: '/marketingagent/check-zone-connections',
      }),
      // No tag caching — always refetch fresh when modal opens
    }),

    getApprovedMarketers: builder.query({
      query: (arg) => {
        if (arg && typeof arg === 'object') {
          return {
            url: '/marketingagent/approved-marketers',
            params: arg,
          };
        }
        return {
          url: '/marketingagent/approved-marketers',
          params: arg ? { userId: arg } : {},
        };
      },
      providesTags: (result, error, arg) => {
        const id = arg && typeof arg === 'object' ? arg.userId : arg;
        return id
          ? [{ type: 'Marketers', id }, 'Marketers']
          : ['Marketers'];
      },
    }),

    createActiveSubscription: builder.mutation({
      query: (payload) => ({
        url: '/activesubscription',
        method: 'POST',
        body: payload,
      }),
    }),

    // ==============================
    // VERIFY DOCS ENDPOINT (user submits their own docs)
    // ==============================
    verifyDocs: builder.mutation({
      query: ({ userId, body }) => ({
        url: `/marketingagent/verifydocs?userId=${userId}`,
        method: 'PATCH',
        body,
      }),
    }),
  }),
});

export const {
  useGetMarketerByMarketerIdQuery,
  useAddOrUpdateMarketerRatingMutation,
  useGetHiredMarketersByShopQuery,
  useGetMarketersByBusinessTypeQuery,
  useGetSubscriptionMarketingQuery,
  useCheckMarketerSubscriptionStatusQuery,
  usePurchaseMarketerPlanMutation,
  useHireMarketerMutation,
  useGetMarketerByUserIdQuery,
  useCreateHireRequestMutation,
  useGetHireRequestStatusQuery,
  useGetSubscriptionPurchaseByShopQuery,
  useGetPurchaseByMarketerQuery,
  useUpdateTaskStatusMutation,
  useLazyDownloadCustomerDataQuery,
  useLazyCheckZoneConnectionsQuery,
  useGetApprovedMarketersQuery,
  useGetPurchasesByUserQuery,
  useCreateActiveSubscriptionMutation,
  useVerifyDocsMutation,
} = marketingApiSlice;
